import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Sidebar } from '@/components/Sidebar';
import { MobileScopeSheet } from '@/components/MobileScopeSheet';
import { SearchResultsView } from '@/components/SearchResultsView';
import { EmailList } from '@/features/email-list/EmailList';
import type { GroupingStrategy } from '@/features/email-list/hooks/useSenderGrouping';
import { SenderDigestView } from '@/components/SenderDigestView';
import { EmailDetail } from '@/features/email-detail/EmailDetail';
import { ThreadDetailView } from '@/features/email-detail/ThreadDetailView';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { resolveEmailContext as resolveEmailContextShared } from '@/lib/emailContext';
import type { Email, EmailActionContext } from '@/types/email';
import type { SenderGroupInfo, ActiveSenderGroup } from '@/features/email-list/types';
import type { ReplyContext } from '@/types/ui';
import type { SyncProgressBarProps } from '@/components/SyncProgressBar';
import { fetchEmail } from '@/api/emails';
import { executeEmailAction } from '@/stores/emailCommands';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useHoverStore } from '@/stores/hoverStore';
import {
  useEmailDetailLoading,
  useEmailStore,
  useEmails,
  useEmailsLoading,
  useSelectedEmail,
  useSelectedEmailId,
  useSelectedThread,
} from '@/stores/emailStore';
import { useEmailActionsStore } from '@/stores/emailActionsStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useOperationStore } from '@/stores/operationStore';
import { useSenderGroupingStore, type InboxMode } from '@/stores/senderGroupingStore';
import { useBriefingStore } from '@/stores/briefingStore';
import { useDragDropStore } from '@/stores/dragDropStore';
import { useContactStore } from '@/stores/contactStore';
import { useSyncStatusStore } from '@/stores/syncStatusStore';
import { useUIStore, type SettingsTab } from '@/stores/uiStore';
import { useSearchStore } from '@/stores/searchStore';
import { mailEvents } from '@/events/mailEvents';
import { MailSplitPane } from './MailSplitPane';
import { buildReplyContextWithFallback } from '@/features/email-detail/utils/replyContext';
import { trainMessageAsSpam, trainMessageAsGood } from '@/api/trainingApi';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import { navigateToTask, navigateToCalendarEvent } from '@/lib/searchNavigation';
import { safePushState, safeReplaceState } from '@/lib/iosPwaNavigation';
import { getHashRoute, parseFullMailRoute, buildMailboxHashPath, type MailRouteParams } from '@/lib/urlRouting';

const hasBrowserEnvironment = typeof window !== 'undefined';

export function MailboxPage() {
  const [pendingRoute, setPendingRoute] = useState<MailRouteParams | null>(() => {
    if (!hasBrowserEnvironment) {
      return null;
    }
    const { pathname, search } = getHashRoute();
    return parseFullMailRoute(pathname, search);
  });
  const lastSyncedUrlRef = useRef<string | null>(null);
  const lastRouteKeyRef = useRef<string | null>(null);
  const lastSenderGroupEmailRef = useRef<string | null>(null);
  const lastBriefingScopeKeyRef = useRef<string | null>(null);
  const isProcessingPopstateRef = useRef<boolean>(false);
  // Timestamp of the last user-initiated email open (tap). The pendingRoute
  // effect's "route has no email → clear selection" correction must NOT fire
  // when a tap landed in the popstate→effect window (U6) — that would close the
  // reader the user just opened.
  const lastUserSelectionAtRef = useRef<number>(0);
  const pendingSenderRestoreRef = useRef<{
    email: string;
    showDigest: boolean;
    // When this restore was armed. The restore-vs-fresh-tap arbitration is an
    // ORDERING question, not a recency one: emails can take seconds to settle
    // before the restore effect runs, so "tapped within the last N ms" misses
    // taps made early in a slow load. Any user selection AFTER this timestamp
    // wins over the restore.
    armedAt: number;
    // When the restored URL also points at an open message/thread inside the
    // group, the selection is deferred to after the group is active (because
    // selectSenderGroup resets the email selection) and replayed from here.
    selection?: {
      emailId: string | null;
      threadId: string | null;
      folderId: string | null;
      candidateAccounts: string[];
    };
  } | null>(null);
  // Track the initial sender from URL to prevent premature removal during restoration
  // Note: restoringFromUrl flag is set in bootstrap.ts before subscriptions fire
  const initialSenderFromUrlRef = useRef<string | null>(
    hasBrowserEnvironment ? new URLSearchParams(getHashRoute().search).get('sender') : null
  );

  const accounts = useAccountStore((state) => state.accounts);
  const accountsLoading = useAccountStore((state) => state.accountsLoading);
  const accountsError = useAccountStore((state) => state.accountsError);
  const selectedAccountId = useAccountStore((state) => state.selectedAccountId);
  const setSelectedAccountId = useAccountStore((state) => state.selectAccount);
  const loadAccounts = useAccountStore((state) => state.loadAccounts);
  const [retryingAccounts, setRetryingAccounts] = useState(false);
  const [renameDialogState, setRenameDialogState] = useState<{
    open: boolean;
    accountId: string;
    folderId: string;
    currentName: string;
    newName: string;
  }>({ open: false, accountId: '', folderId: '', currentName: '', newName: '' });
  const actualAccounts = useMemo(
    () => accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID),
    [accounts]
  );

  const accountColorsMap = useMemo(
    () =>
      actualAccounts.reduce<Record<string, string | null>>((map, account) => {
        map[account.id] = account.color ?? null;
        return map;
      }, {}),
    [actualAccounts]
  );

  const handleRetryAccounts = useCallback(async () => {
    if (accountsLoading || retryingAccounts) {
      return;
    }
    setRetryingAccounts(true);
    try {
      await loadAccounts();
    } catch (error) {
      console.error('Retrying account load failed', error);
    } finally {
      setRetryingAccounts(false);
    }
  }, [accountsLoading, loadAccounts, retryingAccounts]);

  let accountGateContent: ReactNode = null;
  if (accountsLoading && accounts.length === 0) {
    accountGateContent = (
      <div className="flex h-full min-h-screen flex-1 items-center justify-center gap-3 p-6 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm font-medium">Loading accounts…</span>
      </div>
    );
  } else if (!accountsLoading && accountsError && accounts.length === 0) {
    const isRetrying = retryingAccounts;
    accountGateContent = (
      <div className="flex h-full min-h-screen flex-1 items-center justify-center p-6">
        <div className="glass-surface w-full max-w-md space-y-4 rounded-2xl border border-destructive/20 p-6 text-center shadow-lg">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">We couldn’t load your mailboxes</h2>
            <p className="text-sm text-muted-foreground">
              {accountsError}
            </p>
          </div>
          <div className="flex justify-center">
            <Button type="button" onClick={handleRetryAccounts} disabled={isRetrying}>
              {isRetrying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Retrying…
                </>
              ) : (
                'Try again'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const emails = useEmails();
  const selectedEmailId = useSelectedEmailId();
  const selectedEmail = useSelectedEmail();
  const selectedThreadId = useEmailStore((state) => state.selectedThreadId);
  const selectedThread = useSelectedThread();
  const emailsLoading = useEmailsLoading();
  const emailsInitialLoading = useEmailStore((state) => state.emailsInitialLoading);
  const emailDetailLoading = useEmailDetailLoading();
  const loadEmails = useEmailStore((state) => state.loadEmails);
  const loadMoreEmails = useEmailStore((state) => state.loadMoreEmails);
  const selectEmail = useEmailStore((state) => state.selectEmail);
  const selectThread = useEmailStore((state) => state.selectThread);
  const prefetchEmails = useEmailStore((state) => state.prefetchEmails);
  const clearEmailSelection = useEmailStore((state) => state.clearSelection);
  const selectedFolderId = useFolderStore((state) =>
    selectedAccountId ? state.selectedFolderByAccount[selectedAccountId] ?? null : null
  );
  const lastAutoReloadKeyRef = useRef<string | null>(null);

  const setContactsFilter = useContactStore((state) => state.setFilter);
  const approveContact = useContactStore((state) => state.approveContact);
  const blockContact = useContactStore((state) => state.blockContact);
  const loadPendingContacts = useContactStore((state) => state.loadPendingContacts);

  useEffect(() => {
    if (!hasBrowserEnvironment) {
      return;
    }
    const handlePopState = () => {
      isProcessingPopstateRef.current = true;
      const { pathname, search } = getHashRoute();
      const params = new URLSearchParams(search);
      const query = params.get('q');
      const poppedSender = params.get('sender');
      const poppedDigest = params.get('digest');

      // Reconcile the secondary views against the popped URL BEFORE routing
      // (U3/U4): exit search / group / digest when the popped URL no longer
      // carries their param, so Back actually leaves them instead of the sync
      // pass re-asserting the param and swallowing the Back press.
      const searchStore = useSearchStore.getState();
      const uiStore = useUIStore.getState();
      if (query) {
        if (query !== searchStore.query || uiStore.view !== 'search') {
          // Back INTO a search URL — restore the search view.
          useSearchStore.setState((state) => ({
            query,
            loading: true,
            results: [],
            error: null,
            requestId: state.requestId + 1,
          }));
          uiStore.setView('search');
          void useSearchStore.getState().performSearch(query, { skipViewChange: true });
        }
      } else if (uiStore.view === 'search') {
        // Back to a URL with no q — leave the search view (U3).
        searchStore.clearSearch();
        uiStore.setView('mailbox');
      }

      // A pop with no sender param contradicts any armed/active sender restore —
      // disarm it so it can't re-fire and reopen the group (or close the reader)
      // after Back (U1/U2/U8).
      if (!poppedSender) {
        pendingSenderRestoreRef.current = null;
        initialSenderFromUrlRef.current = null;
        if (useSenderGroupingStore.getState().restoringFromUrl) {
          useSenderGroupingStore.setState({ restoringFromUrl: false });
        }
      }
      // A pop with no digest param closes an open digest (U4).
      if (!poppedDigest && useSenderGroupingStore.getState().senderDigestVisible) {
        useSenderGroupingStore.getState().closeSenderDigest();
      }

      // Briefing drill-down scopes reconcile the same way (U3/U4 pattern):
      // Back to a URL without the scope param exits the drill-down; Forward
      // into one re-enters it (item scope only if its item is still loaded —
      // otherwise the sync pass drops the stale param).
      const poppedBriefingKind = params.get('briefingKind');
      const poppedBriefingItem = params.get('briefingItem');
      const briefingStore = useBriefingStore.getState();
      const briefingScope = briefingStore.scope;
      if (briefingScope && !poppedBriefingKind && !poppedBriefingItem) {
        void briefingStore.exitScope();
      } else if (
        poppedBriefingKind &&
        (briefingScope?.type !== 'kind' || briefingScope.kind !== poppedBriefingKind)
      ) {
        briefingStore.enterKindScope(poppedBriefingKind);
      } else if (poppedBriefingItem) {
        const separatorIndex = poppedBriefingItem.indexOf(':');
        const itemAccountId = poppedBriefingItem.slice(0, separatorIndex);
        const itemKey = poppedBriefingItem.slice(separatorIndex + 1);
        if (
          briefingScope?.type !== 'item' ||
          briefingScope.accountId !== itemAccountId ||
          briefingScope.key !== itemKey
        ) {
          const item = briefingStore.entries
            ?.flatMap((entry) => entry.items)
            .find((candidate) => candidate.accountId === itemAccountId && candidate.key === itemKey);
          if (item) {
            void briefingStore.enterItemScope(item);
          }
        }
      }

      const next = parseFullMailRoute(pathname, search);
      if (next) {
        setPendingRoute(next);
        return;
      }
      // The popped URL parses to a NULL mail route ('#/' or '#/?q=…'). The
      // pendingRoute effect would never run, so the guard flag MUST clear here
      // — a stuck flag turns the next email open into a replaceState and
      // silently drops a history entry (F6). Also discard any stale queued
      // route and close an open reader, so a rapid double-Back can't re-commit
      // a stale email route or leave the reader open over '#/' (U5).
      setPendingRoute(null);
      const emailStore = useEmailStore.getState();
      // Review finding 11: mirror the U6 tap guard from the parseable-route
      // branch — a tap that opened a reader inside the popstate window must not be
      // closed by a '#/' pop landing right after it (iOS edge-swipe end).
      if (
        (emailStore.selectedEmailId || emailStore.selectedThreadId) &&
        Date.now() - lastUserSelectionAtRef.current > 700
      ) {
        emailStore.clearSelection();
      }
      isProcessingPopstateRef.current = false;
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const accountFolderCache = useFolderStore((state) => state.accountFolderCache);
  const folders = useFolderStore((state) => state.folders);
  const selectFolder = useFolderStore((state) => state.selectFolder);
  const setFolderVisibility = useFolderStore((state) => state.setFolderVisibility);
  const markFolderAsReadStore = useFolderStore((state) => state.markFolderAsRead);
  const renameFolderStore = useFolderStore((state) => state.renameFolder);
  const selectedFolder = useMemo(() => {
    if (!selectedFolderId) {
      return null;
    }
    return folders.find((folder) => folder.id === selectedFolderId) ?? null;
  }, [folders, selectedFolderId]);
  const selectedFolderRole = selectedFolder?.unifiedRole ?? selectedFolder?.role ?? null;
  const normalizedFolderName = selectedFolder?.name?.trim().toLowerCase() ?? '';
  const normalizedFolderPath = selectedFolder?.path?.trim().toLowerCase() ?? '';
  const normalizedFolderRole =
    typeof selectedFolderRole === 'string' ? selectedFolderRole.toLowerCase() : null;
  const normalizedFolderPathBase = normalizedFolderPath.replace(/\\/g, '/');
  const isDraftFolder =
    normalizedFolderRole === 'drafts' ||
    normalizedFolderName === 'drafts' ||
    normalizedFolderName === 'draft' ||
    normalizedFolderPathBase === 'drafts' ||
    normalizedFolderPathBase === 'draft' ||
    normalizedFolderPathBase.endsWith('/drafts') ||
    normalizedFolderPathBase.endsWith('/draft');
  const isSentFolder = normalizedFolderRole === 'sent' || isDraftFolder;
  const isInboxFolder =
    normalizedFolderRole === 'inbox' ||
    normalizedFolderName === 'inbox' ||
    normalizedFolderPathBase === 'inbox' ||
    normalizedFolderPathBase.endsWith('/inbox');
  const shouldShowPendingSenders =
    Boolean(selectedAccountId && selectedFolderId) && isInboxFolder;
  const groupingStrategy: GroupingStrategy = isSentFolder ? 'recipient' : 'sender';

  useEffect(() => {
    if (!selectedAccountId || !selectedFolderId) {
      void loadPendingContacts({ accountId: null }).catch(() => {});
      return;
    }
    if (!shouldShowPendingSenders) {
      return;
    }
    void loadPendingContacts({
      accountId: selectedAccountId,
      folderId: selectedFolderId,
    }).catch((error) => {
      console.error('Failed to load pending contacts for mailbox change', error);
    });
  }, [loadPendingContacts, selectedAccountId, selectedFolderId, shouldShowPendingSenders]);

  const groupBySender = useSenderGroupingStore((state) => state.groupBySender);
  const inboxMode = useSenderGroupingStore((state) => state.inboxMode);
  const setInboxMode = useSenderGroupingStore((state) => state.setInboxMode);
  const activeSenderGroup = useSenderGroupingStore((state) => state.activeSenderGroup);
  // Briefing drill-down scope, mirrored into the URL (?briefingKind= /
  // ?briefingItem=) so browser Back/Forward navigates it like sender groups.
  const briefingScopeForUrl = useBriefingStore((state) => state.scope);
  const briefingScopeUrlKey = briefingScopeForUrl
    ? briefingScopeForUrl.type === 'kind'
      ? `kind:${briefingScopeForUrl.kind}`
      : `item:${briefingScopeForUrl.accountId}:${briefingScopeForUrl.key}`
    : '';
  const senderDigest = useSenderGroupingStore((state) => state.senderDigest);
  const senderDigestLoading = useSenderGroupingStore((state) => state.senderDigestLoading);
  const senderDigestLoadingMore = useSenderGroupingStore((state) => state.senderDigestLoadingMore);
  const senderDigestVisible = useSenderGroupingStore((state) => state.senderDigestVisible);
  const closeSenderDigest = useSenderGroupingStore((state) => state.closeSenderDigest);
  const exitSenderGroupForNav = useSenderGroupingStore((state) => state.exitSenderGroup);
  const handleDigestMessageVisible = useSenderGroupingStore(
    (state) => state.handleDigestMessageVisible
  );
  const loadMoreSenderDigest = useSenderGroupingStore((state) => state.loadMoreSenderDigest);
  const loadMoreSenderGroupEmails = useSenderGroupingStore((state) => state.loadMoreSenderGroupEmails);
  const selectSenderGroupFromStore = useSenderGroupingStore((state) => state.selectSenderGroup);
  const showSenderDigestFromStore = useSenderGroupingStore((state) => state.showSenderDigest);
  const sentGroupingPreviousPreferenceRef = useRef<InboxMode | null>(null);
  const lastSentFolderIdRef = useRef<string | null>(null);

  // Sent forces the flat mode (recipient grouping is its own opt-in toggle
  // there); the user's inbox mode preference is restored on the way out.
  useEffect(() => {
    if (isSentFolder) {
      if (lastSentFolderIdRef.current !== selectedFolderId) {
        lastSentFolderIdRef.current = selectedFolderId;
        sentGroupingPreviousPreferenceRef.current = inboxMode;
        if (inboxMode !== 'flat') {
          void setInboxMode('flat', { reload: false });
        }
      }
      return;
    }

    if (lastSentFolderIdRef.current) {
      const previousPreference = sentGroupingPreviousPreferenceRef.current;
      lastSentFolderIdRef.current = null;
      sentGroupingPreviousPreferenceRef.current = null;
      if (previousPreference !== null && inboxMode !== previousPreference) {
        void setInboxMode(previousPreference, { reload: false });
      }
    }
  }, [inboxMode, isSentFolder, selectedFolderId, setInboxMode]);

  const dropOnFolder = useDragDropStore((state) => state.dropOnFolder);
  const handleFolderDragEnter = useDragDropStore((state) => state.handleFolderDragEnter);
  const handleFolderDragLeave = useDragDropStore((state) => state.handleFolderDragLeave);
  const activeDropFolderKey = useDragDropStore((state) => state.activeDropFolderKey);
  const draggedItem = useDragDropStore((state) => state.draggedItem);
  const canDropOnFolder = useDragDropStore((state) => state.canDropOnFolder);
  const markOperationPending = useOperationStore((state) => state.markOperationPending);
  const markOperationComplete = useOperationStore((state) => state.markOperationComplete);
  const markOperationError = useOperationStore((state) => state.markOperationError);

  const syncTotals = useSyncStatusStore((state) => state.syncTotals);
  const syncJobs = useSyncStatusStore((state) => state.syncJobs);
  const syncProgressIndicators = useMemo(() => {
    const indicators: SyncProgressBarProps[] = [];
    if (
      syncTotals &&
      syncTotals.progress !== null &&
      syncTotals.progress < 0.999
    ) {
      indicators.push({
        label: 'Sync progress',
        percent: syncTotals.progress,
        detail:
          syncTotals.total > 0
            ? `${syncTotals.processed.toLocaleString()} / ${syncTotals.total.toLocaleString()}`
            : undefined,
        state: 'running',
      });
    }
    syncJobs
      ?.filter((job) => job.state === 'queued' || job.state === 'running')
      .forEach((job) => {
        indicators.push({
          label: job.jobType.replace(/_/g, ' '),
          percent: null,
          state: job.state === 'queued' ? 'queued' : 'running',
        });
      });
    return indicators;
  }, [syncJobs, syncTotals]);
  const sidebarIndicators = syncProgressIndicators;

  const setComposeOpen = useUIStore((state) => state.setComposeOpen);
  const setDraftEmail = useUIStore((state) => state.setDraftEmail);
  const mobileMenuOpen = useUIStore((state) => state.mobileMenuOpen);
  const setMobileMenuOpen = useUIStore((state) => state.setMobileMenuOpen);
  const setSettingsDialogOpen = useUIStore((state) => state.setSettingsDialogOpen);
  const setSettingsInitialTab = useUIStore((state) => state.setSettingsInitialTab);
  const resetEmailSelection = useUIStore((state) => state.resetEmailSelection);
  const view = useUIStore((state) => state.view);
  const mailView = useUIStore((state) => state.mailView);
  const setView = useUIStore((state) => state.setView);
  const setReplyContext = useUIStore((state) => state.setReplyContext);
  const setLabelFilter = useUIStore((state) => state.setLabelFilter);

  // Subscribe to label filter. While labels are hidden by the feature flag the
  // effective filter is always 'all' — the persisted per-folder filter is kept
  // in the store so it comes back when labels are re-enabled.
  const labelFilter = useUIStore((state) => {
    if (!selectedAccountId || !selectedFolderId || !state.labelsVisible) {
      return 'all';
    }
    return state.getLabelFilter(selectedAccountId, selectedFolderId);
  });

  // Search state
  const searchResults = useSearchStore((state) => state.results);
  const searchRelated = useSearchStore((state) => state.related);
  const searchLoading = useSearchStore((state) => state.loading);
  const searchHasMore = useSearchStore((state) => state.hasMore);
  const searchLoadingMore = useSearchStore((state) => state.loadingMore);
  const searchTokens = useSearchStore((state) => state.tokens);
  const searchLoadMore = useSearchStore((state) => state.loadMore);
  const removeSearchToken = useSearchStore((state) => state.removeToken);
  const searchQuery = useSearchStore((state) => state.query);
  const clearSearch = useSearchStore((state) => state.clearSearch);
  const performSearch = useSearchStore((state) => state.performSearch);
  const searchIncludeBlocked = useSearchStore((state) => state.includeBlocked);
  const setSearchIncludeBlocked = useSearchStore((state) => state.setIncludeBlocked);
  const detailAccountIdForUrl = useMemo(() => {
    if (selectedAccountId !== UNIFIED_ACCOUNT_ID) {
      return null;
    }
    return selectedEmail?.accountId ?? selectedThread?.accountId ?? null;
  }, [selectedAccountId, selectedEmail?.accountId, selectedThread?.accountId]);

  // Handle search query and filter in URL on mount
  useEffect(() => {
    if (!hasBrowserEnvironment) return;

    const params = new URLSearchParams(getHashRoute().search);
    const searchQueryParam = params.get('q');

    if (searchQueryParam && searchQueryParam !== searchQuery) {
      // URL has search query but store doesn't - perform search
      void performSearch(searchQueryParam, { skipViewChange: false });
    } else if (!searchQueryParam && view === 'search') {
      // URL lost search query but we're in search view - exit search
      clearSearch();
      setView('mailbox');
    }

    // Apply label filter from URL if present
    const labelParam = params.get('label');
    if (labelParam && selectedAccountId && selectedFolderId) {
      const currentLabel = useUIStore.getState().getLabelFilter(selectedAccountId, selectedFolderId);
      if (currentLabel !== labelParam) {
        setLabelFilter(selectedAccountId, selectedFolderId, labelParam);
      }
    }
  }, []); // Only run on mount

  useEffect(() => {
    if (!pendingRoute) {
      return;
    }
    if (accountsLoading || !accounts.length) {
      return;
    }

    const {
      accountId: routeAccountId,
      folderId: routeFolderId,
      emailId: routeEmailId,
      threadId: routeThreadId,
      detailAccountId: routeDetailAccountId,
      senderGroupEmail: routeSenderGroupEmail,
      autoLabelId: routeAutoLabelId,
    } = pendingRoute;

    const availableAccountIds = accounts.map((account) => account.id);
    const actualAccountIds = actualAccounts.map((account) => account.id);
    const routeAccountValid =
      routeAccountId && availableAccountIds.includes(routeAccountId) ? routeAccountId : null;
    const routeDetailAccountValid =
      routeDetailAccountId && actualAccountIds.includes(routeDetailAccountId)
        ? routeDetailAccountId
        : null;
    const currentAccountValid =
      selectedAccountId && availableAccountIds.includes(selectedAccountId)
        ? selectedAccountId
        : null;

    const targetAccountId = routeAccountValid ?? currentAccountValid ?? availableAccountIds[0] ?? null;

    if (!targetAccountId) {
      return;
    }

    // Apply account from URL if different
    if (selectedAccountId !== targetAccountId) {
      setSelectedAccountId(targetAccountId);
      return;
    }

    // Handle sender group from URL - store pending restoration for when emails are loaded
    // Note: restoringFromUrl flag is already set during component initialization
    if (routeSenderGroupEmail && !activeSenderGroup) {
      pendingSenderRestoreRef.current = {
        email: routeSenderGroupEmail,
        showDigest: pendingRoute.senderDigestVisible,
        armedAt: Date.now(),
      };
    }

    // Apply folder from URL if different
    if (routeFolderId && selectedFolderId !== routeFolderId) {
      selectFolder(targetAccountId, routeFolderId);
    }

    // Handle sender group from URL
    if (!routeSenderGroupEmail && activeSenderGroup) {
      // URL has no sender group but we're in one - exit it
      exitSenderGroupForNav();
    }

    // Handle label filter from URL
    if (routeAutoLabelId && selectedAccountId && selectedFolderId) {
      const currentLabel = labelFilter;
      if (currentLabel !== routeAutoLabelId) {
        setLabelFilter(selectedAccountId, selectedFolderId, routeAutoLabelId);
      }
    } else if (!routeAutoLabelId && selectedAccountId && selectedFolderId && labelFilter !== 'all') {
      // URL has no label filter but we have one active - clear it
      setLabelFilter(selectedAccountId, selectedFolderId, 'all');
    }

    let resolvedFolderId = routeFolderId ?? selectedFolderId ?? null;
    if (!resolvedFolderId) {
      const folderCandidates =
        targetAccountId === selectedAccountId
          ? folders
          : accountFolderCache[targetAccountId] ?? [];
      resolvedFolderId = folderCandidates[0]?.id ?? null;
      if (resolvedFolderId) {
        selectFolder(targetAccountId, resolvedFolderId);
        return;
      }
    }

    const resolveCandidateAccounts = (): string[] => {
      if (routeDetailAccountValid) {
        return [routeDetailAccountValid];
      }
      if (targetAccountId !== UNIFIED_ACCOUNT_ID) {
        return [targetAccountId];
      }
      // Unified deep link: a single 'all' fetch. The backend resolves the owning
      // account from the globally-unique message/thread id, so we no longer guess
      // candidate accounts from the unified folder's sources. That guessing was
      // both flaky (depended on folders/sourceFolders being loaded on a cold
      // refresh) and slow (up to one round-trip per account before a hit).
      return [UNIFIED_ACCOUNT_ID];
    };

    // If we're also restoring a sender group, defer the email/thread selection
    // until AFTER the group becomes active. selectSenderGroup() resets the email
    // selection, so selecting here would immediately be wiped (landing on the
    // group list instead of the open message after a refresh). Stash the same
    // candidates the immediate path would use and let the sender-restore effect
    // replay the selection once the group is in place.
    if (
      pendingSenderRestoreRef.current &&
      ((routeEmailId && resolvedFolderId) || routeThreadId)
    ) {
      pendingSenderRestoreRef.current = {
        ...pendingSenderRestoreRef.current,
        selection: {
          emailId: routeEmailId,
          threadId: routeThreadId,
          folderId: resolvedFolderId,
          candidateAccounts: resolveCandidateAccounts(),
        },
      };
      setPendingRoute(null);
      return;
    }

    // Handle email/thread deep links
    if (routeEmailId) {
      if (!resolvedFolderId) {
        console.warn('Unable to resolve folder for email deep link', pendingRoute);
        setPendingRoute(null);
        return;
      }
      const candidateAccounts = resolveCandidateAccounts();
      if (!candidateAccounts.length) {
        console.warn('Unable to resolve account candidates for email deep link', pendingRoute);
        setPendingRoute(null);
        return;
      }
      const route = pendingRoute;
      // Clear BEFORE starting the async loop: selectEmail eagerly sets
      // selectedEmailId (a dep of this effect), so a still-set pendingRoute
      // re-runs the effect mid-flight and spawns a second resolution loop.
      // Each loop bumps emailSelectRequestId, invalidating the other's fetch —
      // the loser's failure path then wiped the winner's selection, so every
      // unified deep-link refresh toasted "Couldn't open message".
      setPendingRoute(null);
      void (async () => {
        for (const accountId of candidateAccounts) {
          // silentFailure: this loop tries several candidate accounts and
          // surfaces ONE failure itself after exhausting them (N23).
          const attemptId = useEmailStore.getState().emailSelectRequestId + 1;
          const success = await selectEmail(routeEmailId, accountId, resolvedFolderId, {
            ...(routeThreadId ? { threadId: routeThreadId } : {}),
            silentFailure: true,
          });
          if (success) {
            return;
          }
          if (useEmailStore.getState().emailSelectRequestId !== attemptId) {
            // A newer selection superseded this attempt (user clicked an email
            // or navigated) — it owns the detail pane now; fail silently.
            return;
          }
        }
        console.warn('Unable to load email for provided deep link', route);
        toast.error("Couldn't open message");
        // selectEmail set selectedEmailId eagerly per attempt — clear it so the
        // user lands on the list, not an empty detail pane.
        useEmailStore.getState().clearSelection();
      })();
      return;
    }

    if (routeThreadId) {
      const candidateAccounts = resolveCandidateAccounts();
      if (!candidateAccounts.length) {
        console.warn('Unable to resolve account candidates for thread deep link', pendingRoute);
        setPendingRoute(null);
        return;
      }
      const route = pendingRoute;
      // Same re-entrancy guard as the email branch: selectThread eagerly sets
      // selectedThreadId (a dep of this effect).
      setPendingRoute(null);
      void (async () => {
        for (const accountId of candidateAccounts) {
          const attemptId = useEmailStore.getState().emailSelectRequestId + 1;
          const success = await selectThread(routeThreadId, accountId);
          if (success) {
            return;
          }
          if (useEmailStore.getState().emailSelectRequestId !== attemptId) {
            // Superseded by a newer selection — don't toast or clear it.
            return;
          }
        }
        console.warn('Unable to load thread for provided deep link', route);
        // Loud failure + back to the list (N23): the thread deep-link miss was
        // console-only while the pane sat empty.
        toast.error("Couldn't open conversation");
        useEmailStore.getState().clearSelection();
      })();
      return;
    }

    // If URL doesn't have email/thread but we have one selected, clear it
    // This handles back navigation from email detail to list view
    if (!routeEmailId && !routeThreadId && (selectedEmailId || selectedThreadId)) {
      // U6: a tap that landed in the popstate→effect window set a FRESH eager
      // selection — don't close the reader the user just opened. Only clear a
      // selection that predates this navigation (older than the tap window). On
      // iOS the pop lands at edge-swipe-gesture end, exactly when the user taps
      // the next email.
      if (Date.now() - lastUserSelectionAtRef.current > 700) {
        clearEmailSelection();
      }
    }

    // If we only had account/folder params (no email/thread), clear pending route once applied
    if (routeAccountId || routeFolderId) {
      setPendingRoute(null);
    }
  }, [
    accountFolderCache,
    accounts,
    accountsLoading,
    actualAccounts,
    clearEmailSelection,
    folders,
    labelFilter,
    pendingRoute,
    selectEmail,
    selectFolder,
    selectThread,
    selectedAccountId,
    selectedEmailId,
    selectedFolderId,
    selectedThreadId,
    setLabelFilter,
    setSelectedAccountId,
    activeSenderGroup,
    exitSenderGroupForNav,
  ]);

  // Restore sender group from URL after emails are loaded
  useEffect(() => {
    // Only run when we have a pending sender to restore
    const pending = pendingSenderRestoreRef.current;
    if (!pending) {
      return;
    }

    // U2 residual: the user opened an email/thread AFTER the pop that armed
    // this restore. selectSenderGroup below calls resetEmailSelection and would
    // close it — the newer intent wins, so DISARM the restore instead of firing
    // it. Compared against the ARM time (not a recency window): emails can take
    // seconds to settle before this effect runs, and a tap made early in a slow
    // load must still win. A cold URL-restore has no tap (lastUserSelectionAtRef
    // stays 0), so this never blocks a legitimate refresh restore; a tap made
    // BEFORE the pop doesn't block it either (the pop is the newer intent).
    const readerState = useEmailStore.getState();
    if (
      lastUserSelectionAtRef.current > pending.armedAt &&
      (readerState.selectedEmailId || readerState.selectedThreadId)
    ) {
      pendingSenderRestoreRef.current = null;
      initialSenderFromUrlRef.current = null;
      if (useSenderGroupingStore.getState().restoringFromUrl) {
        useSenderGroupingStore.setState({ restoringFromUrl: false });
      }
      return;
    }

    // Wait for emails to finish loading (need at least one load cycle to complete)
    if (emailsLoading || emailsInitialLoading) {
      return;
    }

    // Don't restore if already in a sender group. The user manually entered a
    // group before the async restore fired — drop ALL restore flags so they
    // don't leak `?sender=` into every later URL and freeze folder switches
    // into an empty, never-loading list (U1/U8).
    if (activeSenderGroup) {
      pendingSenderRestoreRef.current = null;
      initialSenderFromUrlRef.current = null;
      if (useSenderGroupingStore.getState().restoringFromUrl) {
        useSenderGroupingStore.setState({ restoringFromUrl: false });
      }
      return;
    }

    // Need account and folder to be selected
    if (!selectedAccountId || !selectedFolderId) {
      return;
    }

    // Keep the ref set until selectSenderGroup completes to prevent other effects from triggering loads
    const showDigest = pending.showDigest;
    const senderEmail = pending.email.toLowerCase();
    const deferredSelection = pending.selection ?? null;

    // Find emails from the sender in the loaded emails (if any)
    const matchingEmails = emails.filter((email) => {
      const fromEmail = email.from?.email?.toLowerCase();
      return fromEmail === senderEmail;
    });

    // Clear the ref before calling selectSenderGroup
    pendingSenderRestoreRef.current = null;

    // Create the sender group - use matching emails if found, otherwise create a minimal
    // group that will trigger a fetch. When restoring from URL, we may not have the sender's
    // emails in the initially loaded page, so we use a high totalMessages to signal that
    // selectSenderGroup should fetch emails from the server.
    const senderGroupInfo: SenderGroupInfo = {
      key: `sender:${senderEmail}`,
      senderEmail: matchingEmails[0]?.from?.email ?? senderEmail,
      senderName: matchingEmails[0]?.from?.name ?? null,
      contactId: matchingEmails[0]?.contactId ?? null,
      emails: matchingEmails,
      // If no matching emails found, assume there are emails to fetch (use high number to trigger fetch)
      totalMessages: matchingEmails.length > 0 ? matchingEmails.length : 100,
      unreadCount: matchingEmails.filter((e) => !e.read).length,
    };

    // Select the sender group
    void selectSenderGroupFromStore(senderGroupInfo).then(async () => {
      // Clear the restoration flags now that restoration is complete
      initialSenderFromUrlRef.current = null;
      useSenderGroupingStore.setState({ restoringFromUrl: false });
      // After selecting the group, show digest if it was visible
      if (showDigest) {
        void showSenderDigestFromStore(senderGroupInfo);
        return;
      }
      // Otherwise re-open the message/thread that was active inside the group.
      // The group selection above cleared the email selection, so we replay the
      // deferred selection now using the same candidates the deep-link path uses.
      // Mirrors the deep-link loops: silentFailure per attempt with ONE toast
      // after exhaustion, and bail silently when a newer selection supersedes
      // the replay (the requestId moved past our attempt's id).
      if (deferredSelection?.emailId && deferredSelection.folderId) {
        for (const accountId of deferredSelection.candidateAccounts) {
          const attemptId = useEmailStore.getState().emailSelectRequestId + 1;
          const success = await selectEmail(
            deferredSelection.emailId,
            accountId,
            deferredSelection.folderId,
            {
              ...(deferredSelection.threadId ? { threadId: deferredSelection.threadId } : {}),
              silentFailure: true,
            }
          );
          if (success) {
            return;
          }
          if (useEmailStore.getState().emailSelectRequestId !== attemptId) {
            return;
          }
        }
        toast.error("Couldn't open message");
        useEmailStore.getState().clearSelection();
      } else if (deferredSelection?.threadId) {
        for (const accountId of deferredSelection.candidateAccounts) {
          const attemptId = useEmailStore.getState().emailSelectRequestId + 1;
          const success = await selectThread(deferredSelection.threadId, accountId);
          if (success) {
            return;
          }
          if (useEmailStore.getState().emailSelectRequestId !== attemptId) {
            return;
          }
        }
        toast.error("Couldn't open conversation");
        useEmailStore.getState().clearSelection();
      }
    });
  }, [
    emails,
    emailsLoading,
    emailsInitialLoading,
    activeSenderGroup,
    selectedAccountId,
    selectedFolderId,
    selectSenderGroupFromStore,
    showSenderDigestFromStore,
    selectEmail,
    selectThread,
  ]);

  useEffect(() => {
    if (!hasBrowserEnvironment || pendingRoute) {
      return;
    }

    // Don't sync URL while there's a pending sender group restoration
    // This prevents the URL sync from removing the sender param before we've restored the group
    if (pendingSenderRestoreRef.current) {
      return;
    }

    // Route key for push decisions uses only emailId, not threadId.
    // Thread loading is a secondary detail that resolves after the initial
    // email selection push and should NOT create extra history entries.
    const makeRouteKey = (emailId: string | null, _threadIdValue: string | null) =>
      emailId ?? '';

    const { pathname: currentPathname, search: currentSearch } = getHashRoute();

    // Never rewrite a URL this page doesn't own. On a cold load of another
    // workspace's route or a fixed sub-view route, MailboxPage's effects run
    // BEFORE AppShell's route-parse effect (child effects fire first), so
    // syncing here would replace '#/triage' / '#/tasks' / … with a mailbox
    // path before the router ever reads it.
    const firstSegment = currentPathname.split('/').filter(Boolean)[0] ?? '';
    if (
      firstSegment === 'tasks' ||
      firstSegment === 'calendar' ||
      firstSegment === 'notes' ||
      firstSegment === 'triage' ||
      firstSegment === 'surveys' ||
      firstSegment === 'subscriptions' ||
      (firstSegment === 'briefing' && mailView !== 'briefing')
    ) {
      return;
    }

    const currentUrl = window.location.hash;
    if (lastSyncedUrlRef.current === null) {
      lastSyncedUrlRef.current = currentUrl;
      lastRouteKeyRef.current = makeRouteKey(selectedEmailId, selectedThreadId);
    }

    const params = new URLSearchParams(currentSearch);
    let mutated = false;

    const removeLegacyParam = (key: string) => {
      if (params.has(key)) {
        params.delete(key);
        mutated = true;
      }
    };

    // Remove legacy query params - the path now contains all routing info
    removeLegacyParam('account');
    removeLegacyParam('folder');
    removeLegacyParam('email');
    removeLegacyParam('thread');
    removeLegacyParam('emailAccountId');
    // Remove current redundant params that duplicate path info
    removeLegacyParam('accountId');
    removeLegacyParam('folderId');
    removeLegacyParam('emailId');
    removeLegacyParam('threadId');
    removeLegacyParam('detailAccountId');

    // The briefing tab owns its own route while no reader is open; opening an
    // email/thread switches to the deep-linkable account path (the push below
    // means Back from the reader returns to '#/briefing').
    const nextPath =
      mailView === 'briefing' && !selectedEmailId && !selectedThreadId
        ? '#/briefing'
        : buildMailboxHashPath(selectedAccountId, selectedFolderId, selectedEmailId, selectedThreadId);

    // Add search query if in search view, remove if not
    if (view === 'search' && searchQuery) {
      params.set('q', searchQuery);
      mutated = true;
    } else {
      removeLegacyParam('q');
    }

    // The unread list filter is gone (round 4) — drop any legacy ?filter param.
    removeLegacyParam('filter');

    // Add sender group if active, or preserve initial sender from URL during restoration
    if (activeSenderGroup?.email) {
      params.set('sender', activeSenderGroup.email);
      mutated = true;
    } else if (initialSenderFromUrlRef.current) {
      // Preserve sender param during restoration - don't remove it yet
      // The sender restoration effect will clear initialSenderFromUrlRef when done
      if (!params.has('sender')) {
        params.set('sender', initialSenderFromUrlRef.current);
        mutated = true;
      }
    } else {
      removeLegacyParam('sender');
    }

    // Add digest visibility if sender digest is open
    if (senderDigestVisible && activeSenderGroup?.email) {
      params.set('digest', 'true');
      mutated = true;
    } else {
      removeLegacyParam('digest');
    }

    // Add briefing drill-down scope (kind or item) if active
    if (briefingScopeForUrl?.type === 'kind') {
      params.set('briefingKind', briefingScopeForUrl.kind);
      removeLegacyParam('briefingItem');
      mutated = true;
    } else if (briefingScopeForUrl?.type === 'item') {
      params.set('briefingItem', `${briefingScopeForUrl.accountId}:${briefingScopeForUrl.key}`);
      removeLegacyParam('briefingKind');
      mutated = true;
    } else {
      removeLegacyParam('briefingKind');
      removeLegacyParam('briefingItem');
    }

    // Add label filter if not default
    if (labelFilter && labelFilter !== 'all') {
      params.set('label', labelFilter);
      mutated = true;
    } else {
      removeLegacyParam('label');
    }

    const nextSearch = params.toString();

    // Check if path changed (compare without # prefix for cleaner comparison)
    const nextPathWithoutHash = nextPath.startsWith('#') ? nextPath.slice(1) : nextPath;
    if (currentPathname !== nextPathWithoutHash) {
      mutated = true;
    }

    if (!mutated) {
      // Even when the URL already matches the current state we must clear the
      // popstate flag here. After a Back/Forward press the reconciling sync
      // normally finds nothing to change and returns through this branch; if we
      // skipped the reset, isProcessingPopstateRef would stay `true` and turn
      // the *next* real navigation into a replaceState, silently dropping a
      // history entry (Back then lands on the wrong page).
      isProcessingPopstateRef.current = false;
      lastSyncedUrlRef.current = window.location.hash;
      lastRouteKeyRef.current = makeRouteKey(selectedEmailId, selectedThreadId);
      lastSenderGroupEmailRef.current = activeSenderGroup?.email ?? '';
      lastBriefingScopeKeyRef.current = briefingScopeUrlKey;
      return;
    }

    // Build complete hash URL
    const nextUrl = `${nextPath}${nextSearch ? `?${nextSearch}` : ''}`;
    const previousKey = lastRouteKeyRef.current ?? '';
    const currentKey = makeRouteKey(selectedEmailId, selectedThreadId);

    // Check for sender group navigation. Push when ENTERING or SWITCHING groups
    // (current non-empty); EXITING a group (current empty) REPLACES so a later
    // Back doesn't re-enter the group just left (U7).
    const previousSenderGroupEmail = lastSenderGroupEmailRef.current ?? '';
    const currentSenderGroupEmail = activeSenderGroup?.email ?? '';
    const isSenderGroupNavigation =
      previousSenderGroupEmail !== currentSenderGroupEmail && currentSenderGroupEmail !== '';

    // Briefing drill-downs follow the same push/replace policy as sender
    // groups: ENTERING/SWITCHING a scope pushes (Back exits it); EXITING
    // replaces (Back doesn't re-enter the scope just left).
    const previousBriefingScopeKey = lastBriefingScopeKeyRef.current ?? '';
    const isBriefingScopeNavigation =
      previousBriefingScopeKey !== briefingScopeUrlKey && briefingScopeUrlKey !== '';

    // Only push to history when:
    // 1. User opens an email FROM A LIST (previousKey empty). Email→email
    //    moves (auto-advance after archive, next/prev) REPLACE instead — a
    //    triage run must not stack every visited email in history; Back from
    //    the reader always returns to the originating list.
    // 2. User enters or exits a sender group
    // 3. It's a meaningful navigation (not initialization)
    // 4. We're not responding to browser back/forward navigation
    const isEmailNavigation = currentKey !== '' && previousKey === '';
    const isRespondingToPopstate = isProcessingPopstateRef.current;
    const shouldPush =
      !isRespondingToPopstate &&
      lastSyncedUrlRef.current !== null &&
      (isEmailNavigation || isSenderGroupNavigation || isBriefingScopeNavigation);

    if (shouldPush) {
      safePushState(nextUrl);
    } else {
      safeReplaceState(nextUrl);
    }

    // Clear the popstate flag after processing
    isProcessingPopstateRef.current = false;

    lastSyncedUrlRef.current = nextUrl;
    lastRouteKeyRef.current = currentKey;
    lastSenderGroupEmailRef.current = currentSenderGroupEmail;
    lastBriefingScopeKeyRef.current = briefingScopeUrlKey;
  }, [detailAccountIdForUrl, pendingRoute, selectedAccountId, selectedEmailId, selectedFolderId, selectedThreadId, view, mailView, searchQuery, activeSenderGroup, labelFilter, senderDigestVisible, briefingScopeUrlKey, briefingScopeForUrl]);

  const emailListAccountColors = useMemo(() => {
    if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
      return accountColorsMap;
    }
    if (selectedAccountId) {
      const account = accounts.find((entry) => entry.id === selectedAccountId);
      if (account) {
        return { [selectedAccountId]: account.color ?? null };
      }
    }
    return undefined;
  }, [accountColorsMap, accounts, selectedAccountId]);

  const markEmailRead = useEmailActionsStore((state) => state.markRead);

  // Button-click actions resolve the target message's real (account, folder) via
  // the same shared resolver the keyboard path uses, so a unified id can never
  // leak into a per-account action and both paths behave identically. The
  // resolver reads the live stores, so this callback needs no reactive deps.
  const resolveEmailContext = useCallback(
    (emailId: string, overrideFolderId?: string | null) =>
      resolveEmailContextShared(emailId, overrideFolderId),
    []
  );

  const handleEmailSelect = useCallback(
    (emailId: string, folderId?: string | null, accountId?: string | null, threadId?: string | null) => {
      if (!selectedAccountId) {
        return;
      }
      // Stamp the tap time so the pendingRoute selection-correction won't close
      // this reader if a popstate lands right after the tap (U6).
      lastUserSelectionAtRef.current = Date.now();
      closeSenderDigest({ forEmailSelection: true });
      const resolved = resolveEmailContext(emailId, folderId ?? null);
      const targetAccountId =
        accountId ??
        (selectedAccountId === UNIFIED_ACCOUNT_ID ? resolved?.accountId ?? null : selectedAccountId);
      const targetFolderId = folderId ?? resolved?.folderId ?? selectedFolderId;
      if (!targetAccountId || !targetFolderId) {
        return;
      }
      if (resolved?.email?.draft) {
        setComposeOpen(true);
        setDraftEmail(resolved.email);
        void fetchEmail(targetAccountId, emailId, targetFolderId)
          .then((full) => {
            setDraftEmail(full);
          })
          .catch((error) => {
            console.error('Failed to load draft contents', error);
          });
        return;
      }
      const options = threadId ? { threadId } : undefined;
      // Failure handling (toast + clearSelection) lives inside selectEmail
      // now (N23) so every caller gets it, not just this one.
      void selectEmail(emailId, targetAccountId, targetFolderId, options);
    },
    [
      closeSenderDigest,
      resolveEmailContext,
      selectEmail,
      selectedAccountId,
      selectedFolderId,
      setDraftEmail,
      setComposeOpen,
    ]
  );

  // One removal handler parametrized by target semantics:
  //  - list rows / swipe / hover  → 'threadRows' (engine expands to members)
  //  - thread per-message / digest cards / single-message detail → 'messages'
  const performRemoval = useCallback(
    (
      kind: 'archive' | 'delete',
      targetKind: 'threadRows' | 'messages',
      emailId: string,
      overrideContext?: EmailActionContext
    ) => {
      // Auto-advance lives in removeEmailsFromStore (A10/D8 consolidation,
      // roadmap item 8): ONE next-email-after-removal helper — pending-sender
      // aware, evaluated against the live projection at removal time. The
      // local neighbor-pick here double-fetched the detail and could
      // re-select a pending-sender row the store pick correctly skips.
      void executeEmailAction(kind, { kind: targetKind, ids: [emailId] }, {
        contextFolderId: overrideContext?.folderId ?? null,
        contextAccountId: overrideContext?.accountId ?? null,
      });
    },
    []
  );

  const handleEmailArchive = useCallback(
    (emailId: string, overrideContext?: EmailActionContext) =>
      performRemoval('archive', 'threadRows', emailId, overrideContext),
    [performRemoval]
  );

  const handleMessageArchive = useCallback(
    (emailId: string, overrideContext?: EmailActionContext) =>
      performRemoval('archive', 'messages', emailId, overrideContext),
    [performRemoval]
  );

  const handleArchiveThread = useCallback(async () => {
    if (!selectedThread) return;

    // All active (non-archived, non-trashed) messages in the thread — the same
    // set the keyboard archive acts on. The engine resolves each message's own
    // folder, applies optimistic removal, records undo, and toasts on failure.
    const activeMessages = selectedThread.messages.filter(
      (message) => !message.isArchived && !message.isTrashed
    );
    if (activeMessages.length === 0) return;

    const ids = activeMessages.map((message) => message.id);
    void executeEmailAction('archive', { kind: 'messages', ids });

    // Navigate back after archiving thread
    clearEmailSelection();
  }, [clearEmailSelection, selectedThread]);

  const handleEmailDelete = useCallback(
    (emailId: string, overrideContext?: EmailActionContext) =>
      performRemoval('delete', 'threadRows', emailId, overrideContext),
    [performRemoval]
  );

  const handleMessageDelete = useCallback(
    (emailId: string, overrideContext?: EmailActionContext) =>
      performRemoval('delete', 'messages', emailId, overrideContext),
    [performRemoval]
  );

  const handleEmailMarkRead = useCallback(
    (emailId: string) => {
      const context = resolveEmailContext(emailId);
      if (!context) {
        return;
      }
      void markEmailRead(emailId, context.accountId, context.folderId, true);
    },
    [markEmailRead, resolveEmailContext]
  );

  // Per-message auto-mark (thread view IntersectionObserver): the reader saw
  // THIS message, not the thread — engine 'messages', never the row wrapper's
  // 'threadRows' (N2: routing through it marked every loaded member read).
  const handleMessageMarkRead = useCallback((emailId: string) => {
    void executeEmailAction('markRead', { kind: 'messages', ids: [emailId] });
  }, []);

  const handleEmailMarkUnread = useCallback(
    (emailId: string) => {
      const context = resolveEmailContext(emailId);
      if (!context) {
        return;
      }
      void markEmailRead(emailId, context.accountId, context.folderId, false);
    },
    [markEmailRead, resolveEmailContext]
  );

  const handleEmailBlockSender = useCallback(
    async (emailId: string) => {
      const context = resolveEmailContext(emailId);
      const contactId =
        context?.email?.contactId ??
        selectedThread?.messages.find((message) => message.id === emailId)?.contactId ??
        null;
      if (!context || !contactId) {
        return;
      }
      markOperationPending(emailId);
      try {
        await blockContact(contactId, { accountId: context.accountId });
        markOperationComplete(emailId);
      } catch (error) {
        console.error('Failed to block sender', error);
        markOperationError(emailId);
        // Rethrow (C10): callers own the user-facing failure handling — the
        // pending-list per-entry revert depends on seeing the rejection.
        throw error;
      }
    },
    [blockContact, markOperationComplete, markOperationError, markOperationPending, resolveEmailContext, selectedThread]
  );

  const handleEmailApproveSender = useCallback(
    async (emailId: string) => {
      const context = resolveEmailContext(emailId);
      const contactId =
        context?.email?.contactId ??
        selectedThread?.messages.find((message) => message.id === emailId)?.contactId ??
        null;
      if (!contactId || !context) {
        return;
      }
      markOperationPending(emailId);
      try {
        await approveContact(contactId, { accountId: context.accountId });
        markOperationComplete(emailId);
      } catch (error) {
        console.error('Failed to approve sender', error);
        markOperationError(emailId);
        // Rethrow (C10): callers own the user-facing failure handling — the
        // pending-list per-entry revert depends on seeing the rejection.
        throw error;
      }
    },
    [approveContact, markOperationComplete, markOperationError, markOperationPending, resolveEmailContext, selectedThread]
  );


  // Hover hygiene: a mailbox switch invalidates any hover state — the rows the
  // cursor was over belong to the previous folder's list.
  useEffect(() => {
    useHoverStore.getState().clearHover();
  }, [selectedAccountId, selectedFolderId]);

  const handleRefreshEmails = useCallback(() => {
    if (!selectedAccountId || !selectedFolderId) {
      return;
    }
    // Route through the reload event so header Refresh + the error-block Retry
    // respect list ownership (sender group / triage) and the skip/defer
    // machinery instead of stamping a plain mailbox list over the group's rows
    // (L1/E6, L8).
    mailEvents.emit('mail:mailbox:reload', {
      accountId: selectedAccountId,
      folderId: selectedFolderId,
      options: { reset: true },
    });
  }, [selectedAccountId, selectedFolderId]);

  const handleLoadMoreEmails = useCallback(() => {
    // When in a sender group, use sender-specific pagination
    const inSenderGroup = useSenderGroupingStore.getState().activeSenderGroup !== null;
    if (inSenderGroup) {
      void loadMoreSenderGroupEmails();
      return;
    }
    if (!selectedAccountId || !selectedFolderId) {
      return;
    }
    void loadMoreEmails(selectedAccountId, selectedFolderId);
  }, [loadMoreEmails, loadMoreSenderGroupEmails, selectedAccountId, selectedFolderId]);

  const handleForceLoadMoreEmails = useCallback(() => {
    if (!selectedAccountId || !selectedFolderId) {
      return;
    }
    void loadMoreEmails(selectedAccountId, selectedFolderId, { force: true });
  }, [loadMoreEmails, selectedAccountId, selectedFolderId]);

  const handleEmailUpdate = useCallback(() => {
    if (!selectedAccountId || !selectedFolderId) {
      return;
    }
    // Route through the reload event (fires after detail read-toggle / inline
    // reply / calendar update) so it respects list ownership instead of
    // clobbering a group/label list with mailbox rows (L1/E6).
    mailEvents.emit('mail:mailbox:reload', {
      accountId: selectedAccountId,
      folderId: selectedFolderId,
      options: { reset: true },
    });
  }, [selectedAccountId, selectedFolderId]);

  const lastMailboxKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedAccountId || !selectedFolderId) {
      lastMailboxKeyRef.current = null;
      return;
    }
    // Skip mailbox load if already in a sender group (the group has its own emails)
    if (activeSenderGroup) {
      return;
    }
    const mailboxKey = `${selectedAccountId}:${selectedFolderId}:${groupBySender ? 'grouped' : 'threaded'}`;
    if (lastMailboxKeyRef.current === mailboxKey) {
      return;
    }
    lastMailboxKeyRef.current = mailboxKey;
    // Clear stale sender caches from previous folder so badge counts are accurate
    useSenderGroupingStore.getState().clearSenderCaches();
    void loadEmails(selectedAccountId, selectedFolderId, {
      reset: true,
    });
  }, [groupBySender, loadEmails, selectedAccountId, selectedFolderId, activeSenderGroup]);

  useEffect(() => {
    if (!selectedAccountId || !selectedFolderId) {
      lastAutoReloadKeyRef.current = null;
      return;
    }
    // Skip auto-reload if already in a sender group
    if (activeSenderGroup) {
      return;
    }
    // Read loading state from Zustand store directly (not React-derived values)
    // to avoid firing a redundant loadEmails when the primary mailbox effect
    // already initiated a load in the same render cycle.
    const storeState = useEmailStore.getState();
    const storeHasEmails = storeState.emails.length > 0;
    const storeIsLoading = storeState.emailsLoading || storeState.emailsInitialLoading;
    if (storeHasEmails || storeIsLoading) {
      if (storeHasEmails) {
        const key = `${selectedAccountId}:${selectedFolderId}`;
        if (lastAutoReloadKeyRef.current === key) {
          lastAutoReloadKeyRef.current = null;
        }
      }
      return;
    }
    const key = `${selectedAccountId}:${selectedFolderId}`;
    if (lastAutoReloadKeyRef.current === key) {
      return;
    }
    lastAutoReloadKeyRef.current = key;
    void loadEmails(selectedAccountId, selectedFolderId, {
      reset: true,
    }).catch((error) => {
      console.error('Auto mailbox reload failed', error);
    });
  }, [
    emails.length,
    emailsInitialLoading,
    emailsLoading,
    groupBySender,
    loadEmails,
    selectedAccountId,
    selectedFolderId,
    activeSenderGroup,
  ]);

  const handleReply = useCallback(
    (context: ReplyContext) => {
      setReplyContext(context);
      setComposeOpen(true);
    },
    [setComposeOpen, setReplyContext]
  );

  const detailAccountId = useMemo(() => {
    if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
      return (
        selectedEmail?.accountId ??
        selectedThread?.accountId ??
        actualAccounts[0]?.id ??
        UNIFIED_ACCOUNT_ID
      );
    }
    return selectedAccountId;
  }, [actualAccounts, selectedAccountId, selectedEmail, selectedThread]);

  // Label the sender the way the row/subscription paths do, so the confirmation
  // names who was acted on rather than an opaque contact id.
  const describeDetailSender = useCallback(
    (contactId: string) => {
      const source =
        (selectedEmail?.contactId === contactId ? selectedEmail : null) ??
        selectedThread?.messages?.find((message) => message.contactId === contactId) ??
        null;
      return source?.from?.name || source?.from?.email || 'sender';
    },
    [selectedEmail, selectedThread]
  );

  const handleDetailApproveSender = useCallback(
    async (contactId: string) => {
      if (!detailAccountId || !contactId) {
        return;
      }
      const operationId = selectedEmail?.id ?? selectedThread?.id ?? contactId;
      const senderLabel = describeDetailSender(contactId);
      markOperationPending(operationId);
      try {
        await approveContact(contactId, { accountId: detailAccountId });
        markOperationComplete(operationId);
        toast.success(`Approved ${senderLabel}`);
      } catch (error) {
        console.error('Failed to approve sender from detail view', error);
        markOperationError(operationId);
        // The optimistic state has already been rolled back by the store; without
        // a toast the reader silently snapped back to its old banner.
        toast.error('Failed to approve sender');
      }
    },
    [approveContact, describeDetailSender, detailAccountId, markOperationComplete, markOperationError, markOperationPending, selectedEmail, selectedThread]
  );

  const handleDetailBlockSender = useCallback(
    async (contactId: string) => {
      if (!detailAccountId || !contactId) {
        return;
      }
      const operationId = selectedEmail?.id ?? selectedThread?.id ?? contactId;
      const senderLabel = describeDetailSender(contactId);
      markOperationPending(operationId);
      try {
        // Blocked FROM the open message: close/advance the reader the way
        // archive and delete do. (Approve deliberately does not — the user is
        // still reading a message they just said they want.)
        await blockContact(contactId, { accountId: detailAccountId, initiator: 'user' });
        markOperationComplete(operationId);
        toast.success(`Blocked ${senderLabel}`);
      } catch (error) {
        console.error('Failed to block sender from detail view', error);
        markOperationError(operationId);
        toast.error('Failed to block sender');
      }
    },
    [blockContact, describeDetailSender, detailAccountId, markOperationComplete, markOperationError, markOperationPending, selectedEmail, selectedThread]
  );

  const openSettings = useCallback(
    (tab: SettingsTab = 'general') => {
      setSettingsInitialTab(tab);
      setSettingsDialogOpen(true);
      setMobileMenuOpen(false);
    },
    [setMobileMenuOpen, setSettingsDialogOpen, setSettingsInitialTab]
  );

  const handleViewPendingContacts = useCallback(() => {
    if (selectedAccountId) {
      void loadPendingContacts({
        accountId: selectedAccountId,
        folderId: selectedFolderId ?? null,
        force: true,
      }).catch((error) => {
        console.error('Failed to refresh pending contacts before opening settings', error);
      });
    }
    setContactsFilter('pending');
    openSettings('contacts');
  }, [loadPendingContacts, openSettings, selectedAccountId, selectedFolderId, setContactsFilter]);

  const handleSidebarNavigate = useCallback(
    (accountId: string, folderId: string) => {
      if (!accountId || !folderId) {
        return;
      }
      // A sidebar folder click while on the briefing tab is a request to LEAVE
      // briefing for that folder — drop back to the mailbox tab first so the
      // inbox-snap invariant below doesn't yank the selection back to inbox.
      if (useUIStore.getState().mailView === 'briefing') {
        useUIStore.getState().setMailView('mailbox');
        // Leave '#/briefing' right here: the URL-sync effect deliberately
        // never rewrites a briefing URL once mailView is 'mailbox' (that same
        // state is a cold '#/briefing' load it must not clobber).
        safeReplaceState(buildMailboxHashPath(accountId, folderId, null, null));
      }
      const sameAccount = accountId === selectedAccountId;
      const sameFolder = sameAccount && folderId === (selectedFolderId ?? null);

      // Exit sender group/digest when navigating to a different folder
      void exitSenderGroupForNav({ skipReload: true });
      // Same for a briefing drill-down: the sidebar click must behave like its
      // Back button. Plain scope clear — the navigation below reloads the
      // mailbox itself (same-folder clicks emit an explicit reload; folder
      // switches reload through the bootstrap subscription).
      useBriefingStore.getState().clearScope();

      resetEmailSelection();
      if (!sameAccount) {
        setSelectedAccountId(accountId);
      }
      selectFolder(accountId, folderId);
      if (sameFolder) {
        mailEvents.emit('mail:mailbox:reload', {
          accountId,
          folderId,
          options: { reset: true },
        });
      }
    },
    [
      exitSenderGroupForNav,
      resetEmailSelection,
      selectFolder,
      selectedAccountId,
      selectedFolderId,
      setSelectedAccountId,
    ]
  );

  const handlePrefetchEmails = useCallback(
    (accountId: string, folderId: string) => {
      void prefetchEmails(accountId, folderId);
    },
    [prefetchEmails]
  );

  // Invariant: the briefing tab is inbox-scoped. Entering it from a non-inbox
  // folder (tab click elsewhere, '#/briefing' deep link) snaps to the unified
  // inbox — same behavior the old briefing inbox-mode had by construction.
  useEffect(() => {
    if (mailView !== 'briefing' || folders.length === 0) {
      return;
    }
    if (!isInboxFolder) {
      handleSidebarNavigate(UNIFIED_ACCOUNT_ID, `${UNIFIED_ACCOUNT_ID}:inbox`);
    }
  }, [mailView, isInboxFolder, folders.length, handleSidebarNavigate]);

  const handleDetailBack = useCallback(() => {
    clearEmailSelection();
  }, [clearEmailSelection]);

  const operationStates = useOperationStore((state) => state.operationStates);
  const isDetailOperationPending = useMemo(() => {
    const operationId = selectedEmail?.id ?? selectedThread?.id ?? selectedEmail?.contactId;
    if (!operationId) {
      return false;
    }
    return operationStates.get(operationId) === 'pending';
  }, [operationStates, selectedEmail, selectedThread]);

  const detailViewActive = useMemo(
    () => Boolean(selectedEmailId || selectedThread || senderDigestVisible),
    [selectedEmailId, selectedThread, senderDigestVisible]
  );

  // Search result handlers
  const handleSearchSelectEmail = useCallback(
    (email: Email) => {
      // Select the email while staying in search view
      handleEmailSelect(email.id, email.folderId, email.accountId, email.threadId ?? null);
    },
    [handleEmailSelect]
  );

  const handleSearchSelectThread = useCallback(
    (thread: { id: string; accountId: string }) => {
      void selectThread(thread.id, thread.accountId);
    },
    [selectThread]
  );

  const handleSearchSelectTask = useCallback(
    (taskId: string) => {
      setView('mailbox');
      navigateToTask(taskId);
    },
    [setView]
  );

  const handleSearchSelectCalendarEvent = useCallback(
    (event: Parameters<NonNullable<React.ComponentProps<typeof SearchResultsView>['onSelectCalendarEvent']>>[0]) => {
      setView('mailbox');
      navigateToCalendarEvent(event);
    },
    [setView]
  );

  const handleSearchSubmit = useCallback(
    (query: string) => {
      if (query.trim()) {
        void performSearch(query, { skipViewChange: false });
      }
    },
    [performSearch]
  );

  const handleCloseSearch = useCallback(() => {
    setView('mailbox');
    clearSearch();
  }, [clearSearch, setView]);

  const handleSearchIncludeBlockedChange = useCallback(
    (value: boolean) => {
      void setSearchIncludeBlocked(value);
    },
    [setSearchIncludeBlocked]
  );

  const handleMarkFoldersAsRead = useCallback(
    async (foldersToMark: Array<{ accountId: string; folderId: string }>) => {
      // Concurrent dispatch: each store call applies its optimistic rows/badge
      // synchronously before its first await, so a unified mark-all clears every
      // account at once instead of account N+1 waiting on account N's round-trip.
      const results = await Promise.allSettled(
        foldersToMark.map(({ accountId, folderId }) => markFolderAsReadStore(accountId, folderId))
      );
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const { accountId, folderId } = foldersToMark[index];
          console.error('Failed to mark folder as read:', { accountId, folderId, error: result.reason });
        }
      });
    },
    [markFolderAsReadStore]
  );

  const handleRenameFolder = useCallback(
    (accountId: string, folderId: string, currentName: string) => {
      setRenameDialogState({
        open: true,
        accountId,
        folderId,
        currentName,
        newName: currentName,
      });
    },
    []
  );

  const handleRenameSubmit = useCallback(async () => {
    const { accountId, folderId, currentName, newName } = renameDialogState;
    if (!newName.trim() || newName.trim() === currentName) {
      setRenameDialogState((prev) => ({ ...prev, open: false }));
      return;
    }
    try {
      await renameFolderStore(accountId, folderId, newName.trim());
    } catch (error) {
      console.error('Failed to rename folder:', { accountId, folderId, error });
    }
    setRenameDialogState((prev) => ({ ...prev, open: false }));
  }, [renameDialogState, renameFolderStore]);

  const sidebar = (
    <Sidebar
      accounts={accounts}
      selectedAccountId={selectedAccountId}
      folders={folders}
      accountFoldersById={accountFolderCache}
      selectedFolderId={selectedFolderId ?? ''}
      onFolderSelect={handleSidebarNavigate}
      onFolderHover={handlePrefetchEmails}
      onOpenSettings={openSettings}
      isMobileOpen={mobileMenuOpen}
      onMobileClose={() => setMobileMenuOpen(false)}
      syncIndicators={sidebarIndicators}
      onEmailDrop={dropOnFolder}
      onFolderDragEnter={handleFolderDragEnter}
      onFolderDragLeave={handleFolderDragLeave}
      activeDropFolderId={activeDropFolderKey}
      isDraggingEmail={Boolean(draggedItem)}
      canDropEmail={canDropOnFolder}
      onSetFolderVisibility={setFolderVisibility}
      onMarkFolderAsRead={handleMarkFoldersAsRead}
      onRenameFolder={handleRenameFolder}
    />
  );

  // Mobile account/folder/layout picker — replaces the sidebar drawer on mail
  // surfaces; opened from the list header scope line.
  const scopeSheet = (
    <MobileScopeSheet
      accounts={accounts}
      folders={folders}
      accountFoldersById={accountFolderCache}
      selectedAccountId={selectedAccountId}
      selectedFolderId={selectedFolderId ?? ''}
      onNavigate={handleSidebarNavigate}
      onOpenSettings={openSettings}
    />
  );

  const renameDialog = (
    <Dialog
      open={renameDialogState.open}
      onOpenChange={(open) => setRenameDialogState((prev) => ({ ...prev, open }))}
    >
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Rename folder</DialogTitle>
          <DialogDescription>
            Enter a new name for this folder.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            value={renameDialogState.newName}
            onChange={(e) => setRenameDialogState((prev) => ({ ...prev, newName: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameSubmit();
              }
            }}
            placeholder="Folder name"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setRenameDialogState((prev) => ({ ...prev, open: false }))}
          >
            Cancel
          </Button>
          <Button onClick={handleRenameSubmit}>
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const emailListProps = useEmailListProps({
    selectedAccountId,
    selectedFolderId,
    groupingStrategy,
    draggedItem,
    showRecipientNames: isSentFolder,
    accountColors: emailListAccountColors,
    showAccountColors: selectedAccountId === UNIFIED_ACCOUNT_ID,
    onEmailSelect: handleEmailSelect,
    onEmailMarkRead: handleEmailMarkRead,
    onEmailMarkUnread: handleEmailMarkUnread,
    onEmailArchive: handleEmailArchive,
    onEmailDelete: handleEmailDelete,
    onEmailBlockSender: handleEmailBlockSender,
    onEmailApproveSender: handleEmailApproveSender,
    onViewPendingContacts: handleViewPendingContacts,
    onLoadMore: handleLoadMoreEmails,
    onForceLoadMore: handleForceLoadMoreEmails,
    onRefresh: handleRefreshEmails,
    showPendingSenders: shouldShowPendingSenders,
    setLabelFilter,
    labelFilter,
    groupBySender,
    activeSenderGroup,
    detailViewActive,
  });

  const liveEmailList = (
    <div className="flex h-full flex-col">
      <EmailList {...emailListProps} />
    </div>
  );
  // P12 (PERF_AUDIT): while the detail pane is active the list is CSS-hidden but
  // still mounted (kept alive so scroll/virtualization survive a back-nav). FREEZE
  // its element reference while hidden so store writes during an open (e.g. the
  // fetched body merging into the index → a fresh `emails` array) don't re-run
  // EmailList's O(n) sender-grouping / pending / label passes for an invisible
  // list. On back-nav we hand back the live element and it catches up in one pass.
  const frozenEmailListRef = useRef(liveEmailList);
  if (!detailViewActive) {
    frozenEmailListRef.current = liveEmailList;
  }
  const emailList = detailViewActive ? frozenEmailListRef.current : liveEmailList;

  // Determine if we should show thread view (thread has multiple messages).
  // Use the declared messageCount, not messages.length, so a PROVISIONAL thread
  // (target message loaded, siblings still streaming — PERF_AUDIT P1) renders the
  // thread shell immediately instead of flashing the single-message view first.
  const showThreadView =
    selectedThread && (selectedThread.messageCount ?? selectedThread.messages.length) > 1;

  const detailContent = senderDigestVisible ? (
    senderDigestLoading ? (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    ) : senderDigest ? (
      <SenderDigestView
        digest={senderDigest}
        onClose={closeSenderDigest}
        onMessageVisible={handleDigestMessageVisible}
        onLoadMore={loadMoreSenderDigest}
        isLoadingMore={senderDigestLoadingMore}
        onArchive={handleMessageArchive}
        onDelete={handleMessageDelete}
        onReply={handleReply}
      />
    ) : (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Digest unavailable. Try refreshing.
      </div>
    )
  ) : showThreadView ? (
    <ThreadDetailView
      thread={selectedThread}
      loading={emailDetailLoading}
      onBack={handleDetailBack}
      onDelete={handleMessageDelete}
      onArchive={handleMessageArchive}
      onArchiveThread={handleArchiveThread}
      onEmailUpdate={handleEmailUpdate}
      onReply={handleReply}
      onMarkRead={handleMessageMarkRead}
      accountId={detailAccountId ?? null}
      highlightEmailId={selectedEmailId}
      onApproveSender={handleDetailApproveSender}
      onBlockSender={handleDetailBlockSender}
      operationPending={isDetailOperationPending}
      isSent={isSentFolder}
    />
  ) : (
    <ComposeAwareEmailDetail
      email={selectedEmail}
      loading={emailDetailLoading}
      onBack={handleDetailBack}
      onDelete={handleMessageDelete}
      onArchive={handleMessageArchive}
      onEmailUpdate={handleEmailUpdate}
      onReply={handleReply}
      accountId={detailAccountId ?? null}
      onApproveSender={handleDetailApproveSender}
      onBlockSender={handleDetailBlockSender}
      operationPending={isDetailOperationPending}
      isSent={isSentFolder}
    />
  );

  if (accountGateContent) {
    return accountGateContent;
  }

  // Render search view when in search mode
  if (view === 'search') {
    return (
      <>
        {renameDialog}
        {scopeSheet}
        <MailSplitPane
          sidebar={sidebar}
          list={
            <SearchResultsView
              results={searchResults}
              related={searchRelated}
              loading={searchLoading}
              tokens={searchTokens}
              onRemoveToken={removeSearchToken}
              hasMore={searchHasMore}
              loadingMore={searchLoadingMore}
              onLoadMore={searchLoadMore}
              query={searchQuery}
              selectedEmailId={selectedEmailId}
              selectedThreadId={selectedThreadId}
              onSelectEmail={handleSearchSelectEmail}
              onSelectThread={handleSearchSelectThread}
              onSelectTask={handleSearchSelectTask}
              onSelectCalendarEvent={handleSearchSelectCalendarEvent}
              onSearchSubmit={handleSearchSubmit}
              onClose={handleCloseSearch}
              includeBlocked={searchIncludeBlocked}
              onIncludeBlockedChange={handleSearchIncludeBlockedChange}
              accountColors={emailListAccountColors}
              accounts={accounts}
            />
          }
          detail={detailContent}
          detailActive={detailViewActive}
        />
      </>
    );
  }

  return (
    <>
      {renameDialog}
      {scopeSheet}
      <MailSplitPane
        sidebar={sidebar}
        list={emailList}
        detail={detailContent}
        detailActive={detailViewActive}
      />
    </>
  );
}

interface ComposeAwareEmailDetailProps {
  email: Email | null;
  loading: boolean;
  onBack: () => void;
  onDelete: (emailId: string, context?: EmailActionContext) => void;
  onArchive: (emailId: string, context?: EmailActionContext) => void;
  onEmailUpdate: () => void;
  onReply: (context: ReplyContext) => void;
  accountId: string | null;
  onApproveSender?: (contactId: string) => void;
  onBlockSender?: (contactId: string) => void;
  operationPending?: boolean;
  isSent?: boolean;
}

function ComposeAwareEmailDetail({
  email,
  loading,
  onBack,
  onDelete,
  onArchive,
  onEmailUpdate,
  onReply,
  accountId,
  onApproveSender,
  onBlockSender,
  operationPending,
  isSent,
}: ComposeAwareEmailDetailProps) {
  return (
    <EmailDetail
      email={email}
      loading={loading}
      onBack={onBack}
      onDelete={onDelete}
      onArchive={onArchive}
      onEmailUpdate={onEmailUpdate}
      onReply={onReply}
      accountId={accountId}
      onApproveSender={onApproveSender}
      onBlockSender={onBlockSender}
      operationPending={operationPending}
      isSent={isSent}
    />
  );
}

type EmailListComponentProps = ComponentProps<typeof EmailList>;
type DraggedItem = ReturnType<typeof useDragDropStore.getState>['draggedItem'];

interface UseEmailListPropsOptions {
  selectedAccountId: string | null;
  selectedFolderId: string | null;
  groupingStrategy: GroupingStrategy;
  draggedItem: DraggedItem | null;
  showRecipientNames: boolean;
  accountColors?: EmailListComponentProps['accountColors'];
  showAccountColors?: boolean;
  onEmailSelect: EmailListComponentProps['onEmailSelect'];
  onEmailMarkRead: EmailListComponentProps['onEmailMarkRead'];
  onEmailMarkUnread: EmailListComponentProps['onEmailMarkUnread'];
  onEmailArchive: EmailListComponentProps['onEmailArchive'];
  onEmailDelete: EmailListComponentProps['onEmailDelete'];
  onEmailBlockSender: EmailListComponentProps['onEmailBlockSender'];
  onEmailApproveSender: EmailListComponentProps['onEmailApproveSender'];
  onViewPendingContacts: EmailListComponentProps['onViewPendingContacts'];
  onEmailReply?: EmailListComponentProps['onEmailReply'];
  onEmailReplyAll?: EmailListComponentProps['onEmailReplyAll'];
  onEmailForward?: EmailListComponentProps['onEmailForward'];
  onCreateTask?: EmailListComponentProps['onCreateTask'];
  onAddSenderLabel?: EmailListComponentProps['onAddSenderLabel'];
  onTrainSpam?: EmailListComponentProps['onTrainSpam'];
  onTrainGood?: EmailListComponentProps['onTrainGood'];
  onLoadMore?: EmailListComponentProps['onLoadMore'];
  onForceLoadMore?: EmailListComponentProps['onForceLoadMore'];
  onRefresh?: EmailListComponentProps['onRefresh'];
  showPendingSenders: boolean;
  setLabelFilter: (accountId: string, folderId: string, labelId: string) => void;
  labelFilter: string | 'all';
  groupBySender: boolean;
  activeSenderGroup: ActiveSenderGroup | null;
  detailViewActive: boolean;
}

function useEmailListProps({
  selectedAccountId,
  selectedFolderId,
  setLabelFilter,
  labelFilter,
  groupBySender,
  activeSenderGroup,
  groupingStrategy,
  draggedItem,
  showRecipientNames,
  accountColors,
  showAccountColors,
  onEmailSelect,
  onEmailMarkRead,
  onEmailMarkUnread,
  onEmailArchive,
  onEmailDelete,
  onEmailBlockSender,
  onEmailApproveSender,
  onEmailReply,
  onEmailReplyAll,
  onEmailForward,
  onCreateTask,
  onAddSenderLabel,
  onTrainSpam,
  onTrainGood,
  onViewPendingContacts,
  onLoadMore,
  onForceLoadMore,
  onRefresh,
  showPendingSenders,
  detailViewActive,
}: UseEmailListPropsOptions): EmailListComponentProps {
  const emails = useEmails();
  const selectedEmailId = useSelectedEmailId();
  const emailsLoading = useEmailsLoading();
  const emailsInitialLoading = useEmailStore((state) => state.emailsInitialLoading);
  const hasMoreEmails = useEmailStore((state) => state.hasMoreEmails);
  const updateEmailInPlace = useEmailStore((state) => state.updateEmailInPlace);
  const setComposeOpen = useUIStore((state) => state.setComposeOpen);
  const setReplyContext = useUIStore((state) => state.setReplyContext);
  const accounts = useAccountStore((state) => state.accounts);

  // Set of user's own email addresses (lowercase) for filtering CC on Reply All
  const accountEmails = useMemo(() => {
    const emails = new Set<string>();
    accounts.forEach((account) => {
      if (account.emailAddress) {
        emails.add(account.emailAddress.toLowerCase());
      }
    });
    return emails;
  }, [accounts]);

  const bulkSelection = useBulkOperationsStore((state) => state.bulkSelection);
  const toggleBulkSelection = useBulkOperationsStore((state) => state.toggleSelection);
  const selectAllVisible = useBulkOperationsStore((state) => state.selectAllVisible);
  const clearSelection = useBulkOperationsStore((state) => state.clearSelection);
  const isBulkEditMode = useBulkOperationsStore((state) => state.bulkEditMode);
  const setBulkEditMode = useBulkOperationsStore((state) => state.setBulkEditMode);
  const createTask = useTodoActionsStore((state) => state.createTask);

  const operationStatesMap = useOperationStore((state) => state.operationStates);
  const operationStates = useMemo(() => {
    const entries: Record<string, 'pending' | 'error'> = {};
    operationStatesMap.forEach((state, id) => {
      if (state === 'pending' || state === 'error') {
        entries[id] = state;
      }
    });
    return entries;
  }, [operationStatesMap]);

  const startEmailDrag = useDragDropStore((state) => state.startEmailDrag);
  const startSenderGroupDrag = useDragDropStore((state) => state.startSenderGroupDrag);
  const endDrag = useDragDropStore((state) => state.endDrag);

  const selectSenderGroup = useSenderGroupingStore((state) => state.selectSenderGroup);
  const exitSenderGroup = useSenderGroupingStore((state) => state.exitSenderGroup);
  const showSenderDigest = useSenderGroupingStore((state) => state.showSenderDigest);
  const archiveSenderGroup = useSenderGroupingStore((state) => state.archiveSenderGroup);
  const deleteSenderGroup = useSenderGroupingStore((state) => state.deleteSenderGroup);
  const handleQuickAction = useSenderGroupingStore((state) => state.handleQuickAction);
  const pendingGroupActions = useSenderGroupingStore((state) => state.pendingGroupActions);
  const isSenderDigestLoading = useSenderGroupingStore((state) => state.senderDigestLoading);
  const isSenderDigestVisible = useSenderGroupingStore((state) => state.senderDigestVisible);
  const senderGroupLoadingMore = useSenderGroupingStore((state) => state.senderGroupLoadingMore);

  const handleLabelFilterChange = useCallback(
    (labelId: string) => {
      if (selectedAccountId && selectedFolderId) {
        setLabelFilter(selectedAccountId, selectedFolderId, labelId);
      }
    },
    [selectedAccountId, selectedFolderId, setLabelFilter]
  );

  const isLoadingMore = (emailsLoading && !emailsInitialLoading) || senderGroupLoadingMore;

  const draggingEmailId =
    draggedItem?.type === 'single' ? draggedItem.primaryEmailId : null;
  const draggingSenderGroupKey = draggedItem?.type === 'group' ? draggedItem.groupKey : null;
  const draggingSenderGroupCount =
    draggedItem?.type === 'group' ? draggedItem.totalCount : null;

  const handleOpenSenderGroupLatestEmail = useCallback(
    (group: SenderGroupInfo, email: Email) => {
      void selectSenderGroup(group);
      onEmailSelect(
        email.id,
        email.folderId ?? null,
        email.accountId ?? null,
        email.threadId ?? null,
      );
    },
    [onEmailSelect, selectSenderGroup]
  );

  const handleListReply = useCallback(
    async (emailId: string, mode: 'reply' | 'replyAll' | 'forward' = 'reply') => {
      // Read the list at CALL time via getState() — depending on the render-scope
      // `emails` would mint a new handler identity on every projection, and these
      // handlers feed every row's memo comparator (P11).
      const target = useEmailStore.getState().emails.find((e) => e.id === emailId);
      if (!target) return;
      // List rows can have an empty from_address; fall back to a detail fetch to
      // recover the reply target so the To field isn't left blank.
      const wasComposeOpen = useUIStore.getState().composeOpen;
      const context = await buildReplyContextWithFallback(target, mode, { accountEmails });
      // X9: if the user opened the composer (a different reply/compose) during
      // the async fallback fetch, don't pop/remount it for this stale message
      // and discard their in-progress compose state.
      if (!wasComposeOpen && useUIStore.getState().composeOpen) {
        return;
      }
      setReplyContext(context);
      setComposeOpen(true);
    },
    [setComposeOpen, setReplyContext, accountEmails]
  );

  const handleTrainSingle = useCallback(
    async (emailId: string, label: 'spam' | 'ham') => {
      const target = useEmailStore.getState().emails.find((e) => e.id === emailId);
      if (!target) return;
      const trainer = label === 'spam' ? trainMessageAsSpam : trainMessageAsGood;
      try {
        const result = await trainer({
          accountId: target.accountId,
          messageId: target.id,
          source: 'email_list',
        });
        // Same status check as the bulk path: only flip the trained flag when
        // the server actually trained (or had already trained) the message.
        const wasTrained = result.status === 'trained' || result.alreadyTrained;
        const timestamp = result?.trainedAt ?? new Date().toISOString();
        if (label === 'spam') {
          updateEmailInPlace(target.id, {
            trainedAsSpam: wasTrained,
            lastTrainedLabel: 'spam',
            lastTrainedAt: timestamp,
            lastTrainedSource: result?.source ?? 'email_list',
          });
        } else {
          updateEmailInPlace(target.id, {
            trainedAsGood: wasTrained,
            lastTrainedLabel: 'ham',
            lastTrainedAt: timestamp,
            lastTrainedSource: result?.source ?? 'email_list',
          });
        }
        if (!wasTrained) {
          toast.error(`Message was not trained as ${label === 'spam' ? 'spam' : 'good'} (${result.status})`);
        }
        return result;
      } catch (error) {
        console.error('Failed to train message', error);
        toast.error(`Failed to train message as ${label === 'spam' ? 'spam' : 'good'}`);
        return undefined;
      }
    },
    [updateEmailInPlace]
  );

  const handleCreateTaskFromList = useCallback(
    async (emailId: string) => {
      const target = useEmailStore.getState().emails.find((e) => e.id === emailId);
      if (!target) return;
      const title = target.subject?.trim() || 'Email task';
      const notesMarkdown = target.snippet || '';
      try {
        await createTask({
          title,
          notesMarkdown,
          emails: [
            {
              messageId: target.id,
              accountId: target.accountId,
            },
          ],
        });
      } catch (error) {
        console.error('Failed to create task from email list', error);
      }
    },
    [createTask]
  );

  // P11 (PERF_AUDIT): hoist the list-action fallbacks to STABLE identities. The
  // emailListProps memo below recomputes on every `emails` change; inline
  // `?? ((id) => …)` fallbacks would hand each row a fresh callback every time and
  // defeat the row memo comparator (which bails on callback-identity change). The
  // source handlers are already useCallback, so these stay referentially stable.
  const fallbackReply = useCallback(
    (emailId: string) => handleListReply(emailId, 'reply'),
    [handleListReply]
  );
  const fallbackReplyAll = useCallback(
    (emailId: string) => handleListReply(emailId, 'replyAll'),
    [handleListReply]
  );
  const fallbackForward = useCallback(
    (emailId: string) => handleListReply(emailId, 'forward'),
    [handleListReply]
  );
  const fallbackTrainSpam = useCallback(
    (emailId: string) => void handleTrainSingle(emailId, 'spam'),
    [handleTrainSingle]
  );
  const fallbackTrainGood = useCallback(
    (emailId: string) => void handleTrainSingle(emailId, 'ham'),
    [handleTrainSingle]
  );

  return useMemo(
    () => ({
      emails,
      selectedEmailId,
      onEmailSelect,
      onLoadMore,
      hasMore: hasMoreEmails,
      isLoadingMore,
      labelFilter,
      onLabelFilterChange: handleLabelFilterChange,
      bulkSelection,
      onToggleBulkSelect: toggleBulkSelection,
      onSelectAllVisible: selectAllVisible,
      onClearSelection: clearSelection,
      isBulkEditMode,
      onBulkEditModeChange: setBulkEditMode,
      onEmailMarkRead,
      onEmailMarkUnread,
      onEmailArchive,
      onEmailDelete,
      onEmailBlockSender,
      onEmailApproveSender,
      onViewPendingContacts,
      isLoadingInitial: emailsInitialLoading,
      operationStates,
      onRefresh,
      accountColors,
      showAccountColors,
      showRecipientNames,
      onEmailDragStart: startEmailDrag,
      onEmailDragEnd: endDrag,
      onSenderGroupDragStart: startSenderGroupDrag,
      onSenderGroupDragEnd: endDrag,
      draggingEmailId,
      draggingSenderGroupKey,
      draggingSenderGroupCount,
      groupBySender,
      groupingStrategy,
      activeSenderGroup,
      onSenderGroupSelect: selectSenderGroup,
      onSenderGroupOpenLatestEmail: handleOpenSenderGroupLatestEmail,
      onExitSenderGroup: exitSenderGroup,
      onShowDigest: showSenderDigest,
      onSenderArchiveAll: archiveSenderGroup,
      onSenderDeleteAll: deleteSenderGroup,
      onSenderGroupAction: handleQuickAction,
      pendingGroupActions,
      isSenderDigestLoading,
      isSenderDigestVisible,
      selectedAccountId,
      selectedFolderId,
      showPendingSenders,
      isListVisible: !detailViewActive,
      onForceLoadMore,
      onEmailReply: onEmailReply ?? fallbackReply,
      onEmailReplyAll: onEmailReplyAll ?? fallbackReplyAll,
      onEmailForward: onEmailForward ?? fallbackForward,
      onTrainSpam: onTrainSpam ?? fallbackTrainSpam,
      onTrainGood: onTrainGood ?? fallbackTrainGood,
      onCreateTask: onCreateTask ?? handleCreateTaskFromList,
      onAddSenderLabel,
    }),
    [
      accountColors,
      activeSenderGroup,
      archiveSenderGroup,
      bulkSelection,
      clearSelection,
      deleteSenderGroup,
      emails,
      draggingEmailId,
      draggingSenderGroupCount,
      draggingSenderGroupKey,
      endDrag,
      labelFilter,
      groupBySender,
      groupingStrategy,
      handleLabelFilterChange,
      handleQuickAction,
      hasMoreEmails,
      isBulkEditMode,
      isLoadingMore,
      isSenderDigestLoading,
      isSenderDigestVisible,
      onEmailArchive,
      onEmailApproveSender,
      onEmailBlockSender,
      onEmailDelete,
      onEmailMarkRead,
      onEmailMarkUnread,
      onEmailSelect,
          onEmailReply,
      onEmailReplyAll,
      onEmailForward,
      onCreateTask,
      onAddSenderLabel,
      onTrainSpam,
      onTrainGood,
      onLoadMore,
      onRefresh,
      onViewPendingContacts,
      operationStates,
      selectAllVisible,
      selectSenderGroup,
      handleOpenSenderGroupLatestEmail,
      pendingGroupActions,
      selectedEmailId,
      setBulkEditMode,
      showAccountColors,
      showRecipientNames,
      showSenderDigest,
      startEmailDrag,
      startSenderGroupDrag,
      toggleBulkSelection,
      exitSenderGroup,
      emailsInitialLoading,
      selectedAccountId,
      selectedFolderId,
      showPendingSenders,
      handleCreateTaskFromList,
      fallbackReply,
      fallbackReplyAll,
      fallbackForward,
      fallbackTrainSpam,
      fallbackTrainGood,
      detailViewActive,
    ]
  );
}
